import { Box } from '@mui/system';
import React, { useContext, useEffect, useRef, useState } from 'react';
import MediumTypography from '../../../components/formlib/MediumTypography';
import dayjs, { Dayjs } from 'dayjs';
import { Grid } from '@mui/material';
import DropdownComponent from '../../../components/formlib/DropdownComponent';
import DatePickerComponent from '../../../components/formlib/DatePickerComponent';
import Textinput from '../../../components/formlib/Textinput';
import CustomRadioButton from '../../../components/formlib/CustomRadioButton';
import CommentTextbox from '../../../components/formlib/CommentTextbox';
import CheckBoxComponent from '../../../components/formlib/CheckBoxComponent';
import InformationModal from '../../ifsp/InformationModal';

import { ReactComponent as DownArrow } from '../../../assets/images/downarrowcolored.svg';
import { ReactComponent as UpArrow } from '../../../assets/images/uparrowcolored.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/infoIcon.svg';
import { useIntl } from 'react-intl';
import { LookUpContext } from '../../../context/LookUpContextProvider';
import { OptionType } from '../../../utils/type';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { updateIfspTransition } from '../../../redux/features/ifsp/ifspTransitionSlice';
import { useIfspTransitionValidationCheckToSubmit } from './ifspTransitionValidationCheck';

const LEAData = [
  {
    id: 1,
    labelId: 'IFSP.LEA.4',
    defaultLabelId: 'Arrange Transition Conference (TC)',
  },
  {
    id: 2,
    labelId: 'IFSP.LEA.1',
    defaultLabelId: 'Sign consent to share information with LEA',
  },
  {
    id: 3,
    labelId: 'IFSP.LEA.2',
    defaultLabelId: 'Determine which sections of EI record to send to LEA',
  },
  {
    id: 4,
    labelId: 'IFSP.LEA.3',
    defaultLabelId:
      'Make referral to LEA/school district (between 2.3 and 2.9)',
  },
  {
    id: 5,
    labelId: 'IFSP.LEA.5',
    defaultLabelId: 'No Referral Made',
  },
];

type IfspTransitionPlanType = {
  isSaveClicked: boolean;
};

const IfspTransitionPlan = ({ isSaveClicked }: IfspTransitionPlanType) => {
  const ifspTransitionDetails = useAppSelector(
    (state) => state.ifspTransitionDetails.ifspTransitionData,
  );
  const ifspMainPageDetails = useAppSelector(
    (state) => state.ifspClientDetails.ifspClientDetails,
  );
  const ifspPermission = useAppSelector((state) => state.ifspPermissionDetails);
  const dispatch = useAppDispatch();
  const [openAccordian, setOpenAccordian] = useState<boolean>(false);
  const [openInfo, setOpenInfo] = useState<boolean>(false);
  const [transitionReasondata, setTransitionReasondata] = useState<
    OptionType[]
  >([]);
  const [leaReferralReasonData, setLeaReferralReasonData] = useState<
    OptionType[]
  >([]);
  const [tpcReasonData, setTpcReasonData] = useState<OptionType[]>([]);
  const [seaStatus, setSeaStatus] = useState<OptionType[]>([]);
  const rctl = useIntl();
  const { lookups } = useContext(LookUpContext);
  const validationsError = useIfspTransitionValidationCheckToSubmit(
    ifspTransitionDetails,
  );
  const parentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (lookups === null) {
      return;
    }

    setTransitionReasondata(() => {
      return lookups.transitionReason
        .filter(
          (r) =>
            r.active ||
            r.code === ifspTransitionDetails.transition.noTransitionPlanReason,
        )
        .map((r) => {
          return {
            label: r.description,
            id: r.code,
          };
        });
    });
    setLeaReferralReasonData(() => {
      return lookups.leaReferralReason.map((r) => {
        return {
          label: r.description,
          id: r.code,
        };
      });
    });
    setTpcReasonData(() => {
      return lookups.tpcReason
        .filter(
          (r) =>
            r.active || r.code === ifspTransitionDetails.transition.noTpcReason,
        )
        .map((r) => {
          return {
            label: r.description,
            id: r.code,
          };
        });
    });
    setSeaStatus(() => {
      return lookups.seaStatus.map((r) => {
        return {
          label: r.description,
          id: r.code,
        };
      });
    });
  }, [lookups]);

  const toYesNo = (value: boolean | null): string | null => {
    if (value === null) return null;
    return value ? 'Yes' : 'No';
  };

  const toTrueFalse = (value: boolean | null): string | null => {
    if (value === null) return null;
    return value ? 'true' : 'false';
  };

  const {
    isTransitionPlanDone,
    isTpcMeetingDone,
    potentiallyLeaEligible,
    notifyChildPotentialEligibility,
    leaConsent,
  } = ifspTransitionDetails.transition;

  const planDoneValue = toYesNo(isTransitionPlanDone);
  const tpcMeetingValue = toYesNo(isTpcMeetingDone);
  const potentiallyEligibleValue = toYesNo(potentiallyLeaEligible);
  const notifyEligibleValue = toTrueFalse(notifyChildPotentialEligibility);
  const leaConsentValue = toYesNo(leaConsent);

  const answerMap: Record<number, boolean> = {
    1: ifspTransitionDetails.transition.arrangeTpc,
    2: ifspTransitionDetails.transition.consentShare,
    3: ifspTransitionDetails.transition.sectionDetermine,
    4: ifspTransitionDetails.transition.makeReferral,
    5: ifspTransitionDetails.transition.noReferralMade,
  };

  const hasValidationError =
    validationsError.consentDate ||
    validationsError.dateOfReferralToLea ||
    validationsError.leaOptOutDate ||
    validationsError.meetingDate ||
    validationsError.noTPCReason ||
    validationsError.noTransitionPlanReason ||
    validationsError.transitionConferenceDeclinedDate ||
    validationsError.transitionPlanDate;

  let borderColor: string;

  if (hasValidationError && isSaveClicked) {
    borderColor = '1px solid red';
  } else if (
    ifspMainPageDetails.transitionTbrUpdateRequired &&
    !ifspMainPageDetails.isTransitionChecked
  ) {
    borderColor = '1px solid #F96300';
  } else {
    borderColor = '1px solid #00C6B8';
  }

  return (
    <Box component="main">
      <InformationModal
        open={openInfo}
        titleLabel="IFSP.Information"
        titleDefaultLabel="Information"
        buttonLabel="IFSP.InfoOkButton"
        buttonDefaultLabel="Ok"
        onClose={() => {
          setOpenInfo(false);
        }}
        infoLabelId="IFSP.TransitionPlanInfo"
        infoDefaultLabel="Early Intervention services may occur for less than the specified period based on the child's age at the time of the eligibility evaluation. All EI services end one day prior to the child's third birthday. Transition planning occurs for all infants and toddlers who are moving on from an EI program. As part of the IFSP, a transition plan is developed to consider and identify the steps and services to support a smooth transition. This process includes activities and tasks discussed by the IFSP Team."
      />
      <Box
        component="section"
        className="p-lg borderRadius4 "
        sx={{
          border: borderColor,
          backgroundColor: '#ECF9F8',
        }}
      >
        <Box component="div" className="flex__justify__space-between">
          {openAccordian ? (
            <Box component="div" className="flex__justify__space-between">
              <MediumTypography
                labelid="IFSP.session7"
                defaultlabel="Transition Plan"
                fontSize="16px"
                fontweight={600}
                textColor="#2A4241"
                className="pr-md"
              />
              <InfoIcon
                className="cursorPointer"
                onClick={() => {
                  setOpenInfo(true);
                }}
              />
            </Box>
          ) : (
            <MediumTypography
              labelid="IFSP.session7"
              defaultlabel="Transition Plan"
              fontSize="18px"
              fontweight={600}
              textColor="#2A4241"
            />
          )}
          {!openAccordian ? (
            <DownArrow
              className="cursorPointer"
              style={{ color: '#008C82' }}
              onClick={() => {
                setOpenAccordian(!openAccordian);
              }}
            />
          ) : (
            <UpArrow
              className="cursorPointer"
              onClick={() => {
                setOpenAccordian(!openAccordian);
              }}
            />
          )}
        </Box>
        {openAccordian && (
          <Box component="div" className="mt-lg">
            <Box
              ref={parentRef}
              style={{
                pointerEvents: !ifspPermission.hasClientEditPermission
                  ? 'none'
                  : 'auto',
                opacity: !ifspPermission.hasClientEditPermission ? '0.5' : '1 ',
              }}
              onKeyDownCapture={(e) => {
                if (!ifspPermission.hasClientEditPermission) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              onFocus={() => {
                if (!ifspPermission.hasClientEditPermission) {
                  if (parentRef.current) parentRef.current.focus();
                }
              }}
            >
              <Grid container rowSpacing={'24px'}>
                <Grid item>
                  <MediumTypography
                    labelid="EICS.TransitionPlan"
                    defaultlabel={`Transition Plan `}
                    fontSize="14px"
                  />
                </Grid>
                <Grid item className="ml-lg">
                  <CustomRadioButton
                    needUnSelect
                    data={[
                      {
                        value: 'Yes',
                        labelId: 'Yes',
                        defaultLabel: 'Yes',
                      },
                      {
                        value: 'No',
                        labelId: 'No',
                        defaultLabel: 'No',
                      },
                    ]}
                    value={planDoneValue}
                    onSelectItem={(value) => {
                      if (value === 'No' || value === null) {
                        dispatch(
                          updateIfspTransition({
                            ...ifspTransitionDetails,
                            transition: {
                              ...ifspTransitionDetails.transition,
                              isTransitionPlanDone:
                                value === 'No' ? false : null,
                              transitionPlanDate: null,
                            },
                          }),
                        );
                      } else {
                        dispatch(
                          updateIfspTransition({
                            ...ifspTransitionDetails,
                            transition: {
                              ...ifspTransitionDetails.transition,
                              isTransitionPlanDone:
                                value === 'Yes' ? true : null,
                            },
                          }),
                        );
                      }
                    }}
                    row={true}
                  />
                </Grid>
                <Grid item width={'384px'}>
                  <DatePickerComponent
                    key={ifspTransitionDetails.transition.transitionPlanDate}
                    className="bg__white"
                    labelid="EICS.TransitionPlandate"
                    disableFuture={false}
                    required={true}
                    defaultlabelid="Date of Transition Plan*"
                    value={
                      ifspTransitionDetails.transition.transitionPlanDate ===
                        '' ||
                      ifspTransitionDetails.transition.transitionPlanDate ===
                        null ||
                      !ifspTransitionDetails.transition.isTransitionPlanDone
                        ? null
                        : dayjs(
                            ifspTransitionDetails.transition.transitionPlanDate,
                          )
                    }
                    handlechange={(dateValue: Dayjs | null) => {
                      const formattedDate =
                        dayjs(dateValue).format('MM/DD/YYYY');
                      if (dateValue) {
                        dispatch(
                          updateIfspTransition({
                            ...ifspTransitionDetails,
                            transition: {
                              ...ifspTransitionDetails.transition,
                              transitionPlanDate: formattedDate,
                            },
                          }),
                        );
                      } else {
                        dispatch(
                          updateIfspTransition({
                            ...ifspTransitionDetails,
                            transition: {
                              ...ifspTransitionDetails.transition,
                              transitionPlanDate: null,
                            },
                          }),
                        );
                      }
                    }}
                    disabledDate={
                      !ifspTransitionDetails.transition.isTransitionPlanDone
                    }
                  />
                  {isSaveClicked && validationsError.transitionPlanDate && (
                    <Box className="loginError">
                      <MediumTypography
                        labelid={
                          validationsError.transitionPlanDateErrorMessageId
                        }
                        defaultlabel={
                          validationsError.transitionPlanDateErrorMessage
                        }
                        className="errorText-md"
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
              <Box>
                <Grid
                  container
                  rowSpacing={'24px'}
                  className="pt-md pb-sm align__items__center"
                >
                  <Grid item width={'615px'}>
                    <DropdownComponent
                      name="noTransitionPlanReason"
                      names={transitionReasondata}
                      value={
                        ifspTransitionDetails.transition.noTransitionPlanReason
                      }
                      defaultlabelid="Reason for “No transition plan” or outside “expected” timeframe"
                      labelid="EICS.TransitionPlanDropdown"
                      handleChange={(value: string) => {
                        if (value) {
                          dispatch(
                            updateIfspTransition({
                              ...ifspTransitionDetails,
                              transition: {
                                ...ifspTransitionDetails.transition,
                                noTransitionPlanReason: value,
                              },
                            }),
                          );
                        } else {
                          dispatch(
                            updateIfspTransition({
                              ...ifspTransitionDetails,
                              transition: {
                                ...ifspTransitionDetails.transition,
                                noTransitionPlanReason: null,
                              },
                            }),
                          );
                        }
                      }}
                      placeholderOnfloatBackgroundColor="#E7F0F0"
                    />
                    <MediumTypography
                      labelid="EICS.TransitionPlanDropDownLabel"
                      sxProps={{ fontStyle: 'italic' }}
                      defaultlabel={
                        'The reason is required as the child is below 27 months and less than 90 days from their 3rd birthday'
                      }
                      fontSize="14px"
                    />
                    {isSaveClicked &&
                      validationsError.noTransitionPlanReason && (
                        <Box>
                          <MediumTypography
                            labelid={
                              validationsError.noTransitionPlanReasonErrorMessageId
                            }
                            defaultlabel={
                              validationsError.noTransitionPlanReasonErrorMessage
                            }
                            sxProps={{
                              color: 'red',
                            }}
                          />
                        </Box>
                      )}
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ borderBottom: '1px solid #97A6A5' }} className="pb-md">
                <MediumTypography
                  labelid="IFSP.responsible"
                  defaultlabel="What do we need to do? Who is responsible?"
                  fontweight={400}
                  textColor="#2A4241"
                />
                <CommentTextbox
                  placeholder={rctl.formatMessage({
                    id: 'IFSP.enterYourComment',
                    defaultMessage: 'Enter your comment',
                  })}
                  className="bg__white translate"
                  rows={5}
                  style={{
                    width: '615px',
                    textAlign: 'justify',
                  }}
                  maxLength={5000}
                  Value={ifspTransitionDetails.transition.needToDoComment}
                  handlechange={(text) =>
                    dispatch(
                      updateIfspTransition({
                        ...ifspTransitionDetails,
                        transition: {
                          ...ifspTransitionDetails.transition,
                          needToDoComment: text || null,
                        },
                      }),
                    )
                  }
                />
              </Box>
              <MediumTypography
                labelid="IFSP.tPC"
                defaultlabel="Transition Conference (TC):"
                fontSize="16px"
                fontweight={600}
                textColor="#2A4241"
                className="pt-md"
              />
              <Box className="flex__ pt-sm align__items__center">
                <MediumTypography
                  labelid="IFSP.meetingDate...."
                  defaultlabel="TC Meeting:"
                  fontweight={400}
                  textColor="#2A4241"
                />
                <CustomRadioButton
                  needUnSelect
                  data={[
                    {
                      value: 'Yes',
                      labelId: 'Yes',
                      defaultLabel: 'Yes',
                    },
                    {
                      value: 'No',
                      labelId: 'No',
                      defaultLabel: 'No',
                    },
                  ]}
                  value={tpcMeetingValue}
                  onSelectItem={(value) => {
                    if (value === 'No' || value === null) {
                      dispatch(
                        updateIfspTransition({
                          ...ifspTransitionDetails,
                          transition: {
                            ...ifspTransitionDetails.transition,
                            isTpcMeetingDone: value === 'No' ? false : null,
                            meetingDate: null,
                          },
                        }),
                      );
                    } else {
                      dispatch(
                        updateIfspTransition({
                          ...ifspTransitionDetails,
                          transition: {
                            ...ifspTransitionDetails.transition,
                            isTpcMeetingDone: value === 'Yes' ? true : null,
                          },
                        }),
                      );
                    }
                  }}
                  row={true}
                  className="ml-md"
                />
              </Box>
              <Box component="div" className="flex__ pt-lg">
                <MediumTypography
                  labelid="IFSP.meetingDate"
                  defaultlabel="Meeting Date:"
                  fontweight={400}
                  textColor="#2A4241"
                  className="mr-md"
                />
                <Box>
                  <Box component="div" sx={{ width: '176px' }}>
                    <DatePickerComponent
                      key={ifspTransitionDetails.transition.meetingDate}
                      className="bg__white"
                      labelid={'IFSP.meetingDate'}
                      defaultlabelid={'Meeting Date'}
                      value={
                        ifspTransitionDetails.transition.meetingDate === '' ||
                        ifspTransitionDetails.transition.meetingDate === null ||
                        ifspTransitionDetails.transition.meetingDate === 'No'
                          ? null
                          : dayjs(ifspTransitionDetails.transition.meetingDate)
                      }
                      handlechange={(dateValue: Dayjs | null) => {
                        if (dateValue) {
                          const formattedDate =
                            dayjs(dateValue).format('MM/DD/YYYY');
                          dispatch(
                            updateIfspTransition({
                              ...ifspTransitionDetails,
                              transition: {
                                ...ifspTransitionDetails.transition,
                                meetingDate: formattedDate,
                              },
                            }),
                          );
                        } else {
                          dispatch(
                            updateIfspTransition({
                              ...ifspTransitionDetails,
                              transition: {
                                ...ifspTransitionDetails.transition,
                                meetingDate: null,
                              },
                            }),
                          );
                        }
                      }}
                      disabledDate={
                        !ifspTransitionDetails.transition.isTpcMeetingDone
                      }
                    />
                  </Box>
                  {isSaveClicked && validationsError.meetingDate && (
                    <Box className="loginError">
                      <MediumTypography
                        labelid={validationsError.meetingDateErrorMessageId}
                        defaultlabel={validationsError.meetingDateErrorMessage}
                        className="errorText-md"
                      />
                    </Box>
                  )}
                </Box>
              </Box>
              <Grid
                container
                rowSpacing={'24px'}
                className="pt-xlg align__items__center"
              >
                <Grid item width={'615px'}>
                  <DropdownComponent
                    name="noTPCReason"
                    names={tpcReasonData}
                    value={ifspTransitionDetails.transition.noTpcReason}
                    defaultlabelid="Reason for 'No TPC' or outside 'expected' time frame"
                    labelid="EICS.ReasonNoTPCOccoured"
                    handleChange={(value: string) => {
                      if (value) {
                        dispatch(
                          updateIfspTransition({
                            ...ifspTransitionDetails,
                            transition: {
                              ...ifspTransitionDetails.transition,
                              noTpcReason: value,
                              transitionConferenceDeclinedDate: null,
                            },
                          }),
                        );
                      } else {
                        dispatch(
                          updateIfspTransition({
                            ...ifspTransitionDetails,
                            transition: {
                              ...ifspTransitionDetails.transition,
                              noTpcReason: null,
                              transitionConferenceDeclinedDate: null,
                            },
                          }),
                        );
                      }
                    }}
                    placeholderOnfloatBackgroundColor="#E7F0F0"
                  />
                  {isSaveClicked && validationsError.noTPCReason && (
                    <Box className="loginError">
                      <MediumTypography
                        labelid={validationsError.noTPCReasonErrorMessageId}
                        defaultlabel={validationsError.noTPCReasonErrorMessage}
                        className="errorText-md"
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={0.3}></Grid>
              </Grid>

              <Box component="div" className="flex__ pt-lg">
                <MediumTypography
                  labelid="IFSP.transitionConferenceFamilyDeclinedDate"
                  defaultlabel="Date Family Declined Transition Conference:"
                  fontweight={400}
                  textColor="#2A4241"
                  className="mr-md"
                />
                <Box>
                  <Box component="div" sx={{ width: '220px' }}>
                    <DatePickerComponent
                      key={
                        ifspTransitionDetails.transition
                          .transitionConferenceDeclinedDate
                      }
                      className="bg__white"
                      labelid={
                        'IFSP.transitionConferenceFamilyDeclinedDateLabel'
                      }
                      defaultlabelid={'Date'}
                      value={
                        ifspTransitionDetails.transition
                          .transitionConferenceDeclinedDate
                          ? dayjs(
                              ifspTransitionDetails.transition
                                .transitionConferenceDeclinedDate,
                            )
                          : null
                      }
                      handlechange={(dateValue: Dayjs | null) => {
                        if (dateValue) {
                          const formattedDate =
                            dayjs(dateValue).format('MM/DD/YYYY');
                          dispatch(
                            updateIfspTransition({
                              ...ifspTransitionDetails,
                              transition: {
                                ...ifspTransitionDetails.transition,
                                transitionConferenceDeclinedDate: formattedDate,
                              },
                            }),
                          );
                        } else {
                          dispatch(
                            updateIfspTransition({
                              ...ifspTransitionDetails,
                              transition: {
                                ...ifspTransitionDetails.transition,
                                transitionConferenceDeclinedDate: null,
                              },
                            }),
                          );
                        }
                      }}
                      required={
                        ifspTransitionDetails.transition.noTpcReason ===
                        'FAMILYDECLINED'
                      }
                      disableFuture
                      disabledDate={
                        ifspTransitionDetails.transition.noTpcReason !==
                        'FAMILYDECLINED'
                      }
                    />
                  </Box>
                  {isSaveClicked &&
                    validationsError.transitionConferenceDeclinedDate && (
                      <Box className="loginError">
                        <MediumTypography
                          labelid={
                            validationsError.transitionConferenceDeclinedDateErrorMessageId
                          }
                          defaultlabel={
                            validationsError.transitionConferenceDeclinedDateErrorMessage
                          }
                          className="errorText-md"
                        />
                      </Box>
                    )}
                </Box>
              </Box>

              {LEAData.map((qus) => {
                return (
                  <Box component="div" key={qus.id}>
                    <Box
                      component="div"
                      className="flex__ pt-sm align__items__center"
                    >
                      <CheckBoxComponent
                        ischecked={answerMap[qus.id]}
                        CheckHandleChange={(value) => {
                          const checked = value.target.checked;

                          dispatch(
                            updateIfspTransition({
                              ...ifspTransitionDetails,
                              transition: {
                                ...ifspTransitionDetails.transition,

                                arrangeTpc:
                                  qus.id === 1
                                    ? checked || null
                                    : ifspTransitionDetails.transition
                                        .arrangeTpc,

                                consentShare:
                                  qus.id === 2
                                    ? checked || null
                                    : ifspTransitionDetails.transition
                                        .consentShare,

                                sectionDetermine:
                                  qus.id === 3
                                    ? checked || null
                                    : ifspTransitionDetails.transition
                                        .sectionDetermine,

                                makeReferral:
                                  qus.id === 4
                                    ? checked || null
                                    : ifspTransitionDetails.transition
                                        .makeReferral,

                                noReferralMade:
                                  qus.id === 5
                                    ? checked || null
                                    : ifspTransitionDetails.transition
                                        .noReferralMade,
                              },
                            }),
                          );
                        }}
                        labelid={qus.labelId}
                        defaultlabelid={qus.defaultLabelId}
                      />
                    </Box>
                    {qus.id === 1 && (
                      <>
                        <Box
                          component="div"
                          className="pt-sm flex__ align__items__center"
                        >
                          <CheckBoxComponent
                            labelid="IFSP.representativeInvited"
                            defaultlabelid="Was the LEA/school district representative invited?"
                            ischecked={
                              ifspTransitionDetails.transition.leaInvited
                            }
                            CheckHandleChange={(value) => {
                              const isChecked = value.target.checked || null;

                              dispatch(
                                updateIfspTransition({
                                  ...ifspTransitionDetails,
                                  transition: {
                                    ...ifspTransitionDetails.transition,
                                    leaInvited: isChecked,
                                  },
                                }),
                              );
                            }}
                          />
                        </Box>
                        <Box
                          component="div"
                          className="flex__ align__items__center pt-md"
                        >
                          <CheckBoxComponent
                            labelid="IFSP.LEA/SchoolParticipate"
                            defaultlabelid="Did the LEA/School District Representative Participate?"
                            ischecked={
                              ifspTransitionDetails.transition.leaRepParticipate
                            }
                            CheckHandleChange={(value) => {
                              const isChecked = value.target.checked || null;

                              dispatch(
                                updateIfspTransition({
                                  ...ifspTransitionDetails,
                                  transition: {
                                    ...ifspTransitionDetails.transition,
                                    leaRepParticipate: isChecked,
                                  },
                                }),
                              );
                            }}
                          />
                        </Box>
                        <Box>
                          <MediumTypography
                            labelid="IFSP.whoParticipated"
                            defaultlabel="Who participated?"
                            fontweight={400}
                            textColor="#2A4241"
                            className="pt-md"
                          />
                          <CommentTextbox
                            placeholder={rctl.formatMessage({
                              id: 'IFSP.enterYourComment',
                              defaultMessage: 'Enter your comment',
                            })}
                            className="bg__white translate"
                            rows={5}
                            style={{
                              width: '615px',
                              textAlign: 'justify',
                            }}
                            maxLength={500}
                            Value={
                              ifspTransitionDetails.transition
                                .whoParticipatedComment
                            }
                            handlechange={(text) =>
                              dispatch(
                                updateIfspTransition({
                                  ...ifspTransitionDetails,
                                  transition: {
                                    ...ifspTransitionDetails.transition,
                                    whoParticipatedComment: text || null,
                                  },
                                }),
                              )
                            }
                          />
                        </Box>
                        <Box
                          sx={{ borderTop: '1px solid #97A6A5' }}
                          className="mt-md"
                        ></Box>
                        <Box
                          component="div"
                          className="flex__ align__items__center pt-md"
                        >
                          <MediumTypography
                            labelid="IFSP.referralToLocalEducationAgency"
                            defaultlabel="Referral to Local Education Agency (LEA/School District)"
                            fontSize="16px"
                            fontweight={600}
                            textColor="#2A4241"
                            className="pr-md"
                          />
                        </Box>
                        <MediumTypography
                          labelid="IFSP.completeThisSectionOnly"
                          defaultlabel="Complete this section ONLY for children who are potentially eligible for LEA"
                          fontSize="14px"
                          sxProps={{
                            fontStyle: 'italic',
                          }}
                          className="pt-sm"
                        />
                        <MediumTypography
                          labelid="IFSP.steps"
                          defaultlabel="Steps that we need to take before age 2.9:"
                          fontSize="16px"
                          fontweight={600}
                          textColor="#2A4241"
                          className="pt-sm pb-sm"
                        />
                        <Grid
                          container
                          rowSpacing={'24px'}
                          className=" align__items__center"
                        >
                          <Grid item>
                            <MediumTypography
                              labelid="EICS.PotentiallyLEAEligible"
                              defaultlabel={`Potentially LEA eligible `}
                              fontSize="14px"
                            />
                          </Grid>
                          <Grid item className="ml-lg">
                            <CustomRadioButton
                              needUnSelect
                              data={[
                                {
                                  value: 'Yes',
                                  labelId: 'Yes',
                                  defaultLabel: 'Yes',
                                },
                                {
                                  value: 'No',
                                  labelId: 'No',
                                  defaultLabel: 'No',
                                },
                              ]}
                              value={potentiallyEligibleValue}
                              onSelectItem={(value) => {
                                if (value === 'No' || value === null) {
                                  dispatch(
                                    updateIfspTransition({
                                      ...ifspTransitionDetails,
                                      transition: {
                                        ...ifspTransitionDetails.transition,
                                        potentiallyLeaEligible:
                                          value === 'No' ? false : null,
                                        notifyChildPotentialEligibility: null,
                                      },
                                    }),
                                  );
                                } else {
                                  dispatch(
                                    updateIfspTransition({
                                      ...ifspTransitionDetails,
                                      transition: {
                                        ...ifspTransitionDetails.transition,
                                        potentiallyLeaEligible:
                                          value === 'Yes' ? true : null,
                                      },
                                    }),
                                  );
                                }
                              }}
                              row={true}
                            />
                          </Grid>
                        </Grid>
                        {ifspTransitionDetails.transition
                          .potentiallyLeaEligible ? (
                          <Box component="div">
                            <CustomRadioButton
                              data={[
                                {
                                  value: 'true',
                                  labelId: 'IFSP.notify',
                                  defaultLabel: `Notify the LEA/SEA of my child's potential eligibility`,
                                },
                                {
                                  value: 'false',
                                  labelId: 'IFSP.noNotify',
                                  defaultLabel:
                                    'Parent choose to opt out of notification',
                                },
                              ]}
                              value={notifyEligibleValue}
                              onSelectItem={(value) => {
                                if (value) {
                                  dispatch(
                                    updateIfspTransition({
                                      ...ifspTransitionDetails,
                                      transition: {
                                        ...ifspTransitionDetails.transition,
                                        notifyChildPotentialEligibility:
                                          value === 'true',
                                      },
                                    }),
                                  );
                                } else if (value === null) {
                                  dispatch(
                                    updateIfspTransition({
                                      ...ifspTransitionDetails,
                                      transition: {
                                        ...ifspTransitionDetails.transition,
                                        notifyChildPotentialEligibility: null,
                                      },
                                    }),
                                  );
                                }
                              }}
                              row={false}
                              paddingY="4px"
                            />
                          </Box>
                        ) : null}
                      </>
                    )}
                    {qus.id === 2 && (
                      <>
                        <Box className="flex__ pt-sm align__items__center">
                          <MediumTypography
                            labelid={'IFSP.dateOfConsen...t'}
                            defaultlabel={'LEA Consent:'}
                            fontweight={400}
                            textColor="#2A4241"
                            className="pr-md"
                          />
                          <CustomRadioButton
                            needUnSelect
                            data={[
                              {
                                value: 'Yes',
                                labelId: 'Yes',
                                defaultLabel: 'Yes',
                              },
                              {
                                value: 'No',
                                labelId: 'No',
                                defaultLabel: 'No',
                              },
                            ]}
                            value={leaConsentValue}
                            onSelectItem={(value) => {
                              if (value === 'No' || value === null) {
                                dispatch(
                                  updateIfspTransition({
                                    ...ifspTransitionDetails,
                                    transition: {
                                      ...ifspTransitionDetails.transition,
                                      leaConsent: value === 'No' ? false : null,
                                      consentDate: null,
                                    },
                                  }),
                                );
                              } else {
                                dispatch(
                                  updateIfspTransition({
                                    ...ifspTransitionDetails,
                                    transition: {
                                      ...ifspTransitionDetails.transition,
                                      leaConsent: value === 'Yes' ? true : null,
                                    },
                                  }),
                                );
                              }
                            }}
                            row={true}
                          />
                        </Box>
                        {ifspTransitionDetails.transition.leaConsent && (
                          <Box component="div" className="pt-sm flex__">
                            <MediumTypography
                              labelid={'IFSP.dateOfConsent'}
                              defaultlabel={'Date of Consent:'}
                              fontweight={400}
                              textColor="#2A4241"
                              className="pr-md"
                            />
                            <Box>
                              <Box component="div" sx={{ width: '176px' }}>
                                <DatePickerComponent
                                  key={
                                    ifspTransitionDetails.transition.consentDate
                                  }
                                  className="bg__white"
                                  labelid={'IFSP.dateOfConsent'}
                                  defaultlabelid={'Date of Consent'}
                                  value={
                                    ifspTransitionDetails.transition
                                      .consentDate === '' || null
                                      ? null
                                      : dayjs(
                                          ifspTransitionDetails.transition
                                            .consentDate,
                                        )
                                  }
                                  handlechange={(dateValue: Dayjs | null) => {
                                    if (dateValue) {
                                      const formattedDate =
                                        dayjs(dateValue).format('MM/DD/YYYY');
                                      dispatch(
                                        updateIfspTransition({
                                          ...ifspTransitionDetails,
                                          transition: {
                                            ...ifspTransitionDetails.transition,
                                            consentDate: formattedDate,
                                          },
                                        }),
                                      );
                                    } else {
                                      dispatch(
                                        updateIfspTransition({
                                          ...ifspTransitionDetails,
                                          transition: {
                                            ...ifspTransitionDetails.transition,
                                            consentDate: null,
                                          },
                                        }),
                                      );
                                    }
                                  }}
                                />
                              </Box>
                              {isSaveClicked &&
                                validationsError.consentDate && (
                                  <Box className="loginError">
                                    <MediumTypography
                                      labelid={
                                        validationsError.consentDateErrorMessageId
                                      }
                                      defaultlabel={
                                        validationsError.consentDateErrorMessage
                                      }
                                      className="errorText-md"
                                    />
                                  </Box>
                                )}
                            </Box>
                          </Box>
                        )}
                      </>
                    )}
                    {qus.id === 3 && (
                      <Box
                        component="div"
                        className="pt-sm flex__ align__items__center"
                      >
                        <MediumTypography
                          labelid={'IFSP.specify'}
                          defaultlabel={'Specify:'}
                          fontweight={400}
                          textColor="#2A4241"
                          className="pr-md"
                        />
                        <Textinput
                          placeHolder="Specify here.."
                          sxProps={{
                            width: '553px',
                            backgroundColor: '#ffffff',
                          }}
                          Value={
                            ifspTransitionDetails.transition.specifyComment
                          }
                          inputProps={{
                            className: 'translate',
                            maxLength: 500,
                          }}
                          handlechange={(text) =>
                            dispatch(
                              updateIfspTransition({
                                ...ifspTransitionDetails,
                                transition: {
                                  ...ifspTransitionDetails.transition,
                                  specifyComment: text || null,
                                },
                              }),
                            )
                          }
                        />
                      </Box>
                    )}
                    {qus.id === 4 && (
                      <>
                        <Box component="div" className="pt-sm flex__">
                          <MediumTypography
                            labelid={'IFSP.dateOfReferralLea'}
                            defaultlabel={'Date of Referral to LEA:'}
                            fontweight={400}
                            textColor="#2A4241"
                            className="pr-md"
                          />
                          <Box>
                            <Box component="div" sx={{ width: '220px' }}>
                              <DatePickerComponent
                                key={
                                  ifspTransitionDetails.transition
                                    .dateOfReferralToLea
                                }
                                className="bg__white"
                                labelid={'IFSP.dateOfReferralLeaPicker'}
                                defaultlabelid={'Date of Referral to LEA'}
                                value={
                                  ifspTransitionDetails.transition
                                    .dateOfReferralToLea === '' || null
                                    ? null
                                    : dayjs(
                                        ifspTransitionDetails.transition
                                          .dateOfReferralToLea,
                                      )
                                }
                                handlechange={(dateValue: Dayjs | null) => {
                                  if (dateValue) {
                                    const formattedDate =
                                      dateValue.format('MM/DD/YYYY');
                                    dispatch(
                                      updateIfspTransition({
                                        ...ifspTransitionDetails,
                                        transition: {
                                          ...ifspTransitionDetails.transition,
                                          dateOfReferralToLea: formattedDate,
                                        },
                                      }),
                                    );
                                  } else {
                                    dispatch(
                                      updateIfspTransition({
                                        ...ifspTransitionDetails,
                                        transition: {
                                          ...ifspTransitionDetails.transition,
                                          dateOfReferralToLea: null,
                                        },
                                      }),
                                    );
                                  }
                                }}
                              />
                            </Box>
                            {isSaveClicked &&
                              validationsError.dateOfReferralToLea && (
                                <Box className="loginError">
                                  <MediumTypography
                                    labelid={
                                      validationsError.dateOfReferralToLeaErrorMessageId
                                    }
                                    defaultlabel={
                                      validationsError.dateOfReferralToLeaErrorMessage
                                    }
                                    className="errorText-md"
                                  />
                                </Box>
                              )}
                          </Box>
                        </Box>
                        <Box>
                          <Grid
                            container
                            rowSpacing={'24px'}
                            className="pt-xlg align__items__center"
                          >
                            <Grid item width={'615px'}>
                              <DropdownComponent
                                name="leaReferralReason"
                                names={leaReferralReasonData}
                                value={
                                  ifspTransitionDetails.transition
                                    .outsideTpcReason
                                }
                                defaultlabelid="Reason if LEA referral is outside of standard timeframe (27-33 mo)"
                                labelid="EICS.LEADropDown"
                                handleChange={(value: string) => {
                                  dispatch(
                                    updateIfspTransition({
                                      ...ifspTransitionDetails,
                                      transition: {
                                        ...ifspTransitionDetails.transition,
                                        outsideTpcReason: value || null,
                                      },
                                    }),
                                  );
                                }}
                                placeholderOnfloatBackgroundColor="#E7F0F0"
                                disabled
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </>
                    )}
                  </Box>
                );
              })}
              <Box>
                <Grid
                  container
                  rowSpacing={'24px'}
                  className="pt-md align__items__center"
                >
                  <Grid item width={'615px'}>
                    <DropdownComponent
                      name="leaReferralReason"
                      names={seaStatus}
                      value={ifspTransitionDetails.transition.seaStatus}
                      defaultlabelid="SEA Status"
                      labelid="IFSP.SEAStatus"
                      handleChange={(value: string) => {
                        if (value !== 'OPTOUT') {
                          dispatch(
                            updateIfspTransition({
                              ...ifspTransitionDetails,
                              transition: {
                                ...ifspTransitionDetails.transition,
                                seaStatus: value || null,
                                leaOptOutDate: null,
                              },
                            }),
                          );
                        } else {
                          dispatch(
                            updateIfspTransition({
                              ...ifspTransitionDetails,
                              transition: {
                                ...ifspTransitionDetails.transition,
                                seaStatus: value || null,
                              },
                            }),
                          );
                        }
                      }}
                      placeholderOnfloatBackgroundColor="#E7F0F0"
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box component="div" className="pt-md flex__">
                <MediumTypography
                  labelid={'IFSP.LeaOptoutDateTitle'}
                  defaultlabel={'LEA Opt-out Notification date:'}
                  fontweight={400}
                  textColor="#2A4241"
                  className="pr-md"
                />
                <Box component="div" sx={{ width: '260px' }}>
                  <DatePickerComponent
                    key={ifspTransitionDetails.transition.leaOptOutDate}
                    required={
                      ifspTransitionDetails.transition.seaStatus === 'OPTOUT'
                    }
                    className="bg__white"
                    labelid={'IFSP.LeaOptoutDate'}
                    defaultlabelid={'LEA Opt-out Notification date'}
                    value={
                      ifspTransitionDetails.transition.leaOptOutDate === null ||
                      ifspTransitionDetails.transition.leaOptOutDate === ''
                        ? null
                        : dayjs(ifspTransitionDetails.transition.leaOptOutDate)
                    }
                    handlechange={(value) => {
                      if (value) {
                        const formattedDate = dayjs(value).format('MM/DD/YYYY');
                        dispatch(
                          updateIfspTransition({
                            ...ifspTransitionDetails,
                            transition: {
                              ...ifspTransitionDetails.transition,
                              leaOptOutDate: formattedDate,
                            },
                          }),
                        );
                      } else {
                        dispatch(
                          updateIfspTransition({
                            ...ifspTransitionDetails,
                            transition: {
                              ...ifspTransitionDetails.transition,
                              leaOptOutDate: null,
                            },
                          }),
                        );
                      }
                    }}
                    disabledDate={
                      ifspTransitionDetails.transition.seaStatus !== 'OPTOUT'
                    }
                  />
                  {isSaveClicked && validationsError.leaOptOutDate && (
                    <Box className="loginError">
                      <MediumTypography
                        labelid={validationsError.leaOptOutDateErrorMessageId}
                        defaultlabel={
                          validationsError.leaOptOutDateErrorMessage
                        }
                        className="errorText-md"
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default IfspTransitionPlan;
